import {
  Button,
  Typography,
  Box,
  FormControlLabel,
  FormGroup,
  Switch,
  Table,
  TableBody,
  TableHead,
  TextField,
} from '@mui/material';
import { floatVal } from '@jamesgmarks/utilities';
import { roundFloatToCents } from '@hydra/internal';
import { toDollarAmount } from '../../../../app-utils';
import { StyledTableCell, StyledTableRow } from '../../../parts/mui/StyledTables';
import { SelectedCredit } from './types';
import { ISubscriptions } from '@llws/lift-entity-interfaces';
import { ICredit } from '../../../../interfaces/ICredit';

interface ReviewModeProps {
  selectedCredits: SelectedCredit[];
  isSubmitting: boolean;
  creditNoteComment: string;
  displayCreditNoteCommentOnPdf: boolean;
  onCreditAmountUpdate: (creditIndex: number, newAmount: string) => void;
  onCreditDescriptionUpdate: (creditIndex: number, newDescription: string) => void;
  onCreditRemove: (creditIndex: number) => void;
  onCreditNoteCommentChange: (newComment: string) => void;
  onDisplayCreditNoteCommentOnPdfChange: (displayOnPdf: boolean) => void;
  onCreateCredits: () => void;
}

export const ReviewMode = ({
  selectedCredits,
  isSubmitting,
  creditNoteComment,
  displayCreditNoteCommentOnPdf,
  onCreditAmountUpdate,
  onCreditDescriptionUpdate,
  onCreditRemove,
  onCreditNoteCommentChange,
  onDisplayCreditNoteCommentOnPdfChange,
  onCreateCredits,
}: ReviewModeProps) => {
  if (selectedCredits.length === 0) {
    return (
      <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No credits selected. Please go back and select some charges.
      </Typography>
    );
  }  // Group credits by subscription for better organization
  const creditsBySubscription = selectedCredits.reduce<
  Record<number, { subscription: ISubscriptions; credits: ICredit[] }>
  >((acc, sc) => {
    const subId = sc.subscription.id;
    if (!subId) {
      throw new Error('Subscription ID is missing from selected credit');
    }
    return {
      ...acc,
      [subId]: {
        subscription: sc.subscription,
        credits: [...(acc[subId]?.credits ?? []), sc.credit],
      },
    };
  }, {});

  // Helper function to calculate current credit amount (pre-tax)
  const getCurrentCreditAmount = (credit: ICredit): number => {
    return floatVal(credit.amount) || 0; // credit.amount now contains pre-tax amount
  };

  // Helper function to get tax amount
  const getTaxAmount = (credit: ICredit): number => {
    return floatVal(credit.taxAmount) || 0;
  };

  // Helper function to calculate maximum allowed credit amount (pre-tax)
  const getMaxAllowedCreditAmount = (selectedCredit: SelectedCredit): number => {
    return selectedCredit.originalAmount; // originalAmount now contains pre-tax amount
  };

  // Calculate total pre-tax amount for summary using parent state
  const totalPreTaxAmount = selectedCredits.reduce((sum, sc) => sum + getCurrentCreditAmount(sc.credit), 0);

  const totalTaxAmount = selectedCredits.reduce((sum, sc) => sum + getTaxAmount(sc.credit), 0);

  // Check if any credit amounts exceed their original amounts using parent state
  const hasInvalidAmounts = selectedCredits.some((sc) => {
    const currentCreditAmount = getCurrentCreditAmount(sc.credit);
    const maxAllowedAmount = getMaxAllowedCreditAmount(sc);
    return currentCreditAmount > maxAllowedAmount;
  });

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and edit your selected credits before creating credit notes.
        Credit amounts are shown before tax. You can adjust the pre-tax amounts
        (up to the original pre-tax amount) and descriptions.
      </Typography>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Review Summary
        </Typography>
        <Typography variant="body2">
          <strong>Total Credits:</strong> {selectedCredits.length}
        </Typography>
        <Typography variant="body2">
          <strong>Subtotal Amount:</strong> {toDollarAmount(totalPreTaxAmount)}
        </Typography>
        <Typography variant="body2">
          <strong>Tax Amount:</strong> {toDollarAmount(totalTaxAmount)}
        </Typography>
        <Typography variant="body2">
          <strong>Total Amount:</strong> {toDollarAmount(totalPreTaxAmount + totalTaxAmount)}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Note"
          multiline
          rows={3}
          value={creditNoteComment}
          onChange={(e) => onCreditNoteCommentChange(e.target.value)}
        />
        <FormGroup sx={{ mt: 1 }}>
          <FormControlLabel
            control={(
              <Switch
                checked={displayCreditNoteCommentOnPdf}
                onChange={(e) => onDisplayCreditNoteCommentOnPdfChange(e.target.checked)}
              />
            )}
            label="Display on PDF"
          />
        </FormGroup>
      </Box>

      {Object.values(creditsBySubscription).map((group) => (
        <Box key={group.subscription.id} sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {group.subscription.invoiceDescription}
          </Typography>

          <Table size="small">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Invoice #</StyledTableCell>
                <StyledTableCell>Charge ID</StyledTableCell>
                <StyledTableCell>Original Amount</StyledTableCell>
                <StyledTableCell>Credit Subtotal</StyledTableCell>
                <StyledTableCell>Tax Amount</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {group.credits.map((credit) => {
                // Find the full SelectedCredit object for this credit
                const selectedCredit = selectedCredits.find(sc => sc.credit === credit);
                if (!selectedCredit) {
                  throw new Error('Selected credit not found for credit in group');
                }

                const creditIndex = selectedCredits.indexOf(selectedCredit);
                const currentCreditAmount = getCurrentCreditAmount(credit);
                const maxAllowedAmount = getMaxAllowedCreditAmount(selectedCredit);
                const uniqueKey = `credit-${creditIndex}`;

                return (
                  <StyledTableRow key={uniqueKey}>
                    {/* Prefer the denormalised invoiceNumber field; fall back to the nested invoice object, then 'N/A'. */}
                    <StyledTableCell>{credit.invoiceNumber ?? credit.invoice?.invoiceNumber ?? 'N/A'}</StyledTableCell>
                    <StyledTableCell>{credit.generatedSubscriptionId}</StyledTableCell>
                    <StyledTableCell>
                      {toDollarAmount(maxAllowedAmount)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        value={credit.amount ?? ''}
                        onChange={(e) => {
                          onCreditAmountUpdate(creditIndex, e.target.value);
                        }}
                        onBlur={(e) => {
                          const newPreTaxAmount = floatVal(e.target.value);
                          if (Number.isFinite(newPreTaxAmount)) {
                            onCreditAmountUpdate(creditIndex, roundFloatToCents(newPreTaxAmount).toString());
                          }
                        }}
                        sx={{ width: 120 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      {toDollarAmount(getTaxAmount(credit))}
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        size="small"
                        value={credit.description}
                        onChange={(e) => onCreditDescriptionUpdate(creditIndex, e.target.value)}
                        sx={{ minWidth: 200 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => onCreditRemove(creditIndex)}
                      >
                        Remove
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ))}

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          color="success"
          variant="contained"
          onClick={onCreateCredits}
          disabled={isSubmitting || selectedCredits.length === 0 || hasInvalidAmounts}
          size="large"
        >
          {isSubmitting ? 'Splitting...' : `Split into Credit Notes`}
        </Button>
        {hasInvalidAmounts && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Please fix credit amounts that exceed their original values before proceeding.
          </Typography>
        )}
      </Box>
    </>
  );
};
